import React, { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { LogOut, ChevronDown } from "lucide-react";
import Sidebar, { ADMIN_NAV_SECTIONS } from "./AdminSidebar";
import AdminPlaceholder from "./AdminPlaceholder";
import "./admin.css";

// Flatten the sidebar's sections once, so the layout can look up the current
// page's label from the URL without duplicating that list here. Items with a
// "children" submenu aren't routes themselves, so their children are flattened
// in instead of the parent.
const ALL_ITEMS = ADMIN_NAV_SECTIONS.flatMap((s) =>
  s.items.flatMap((item) => (item.children ? item.children : [item]))
);

function currentLabelFromPath(pathname) {
  const slug = pathname.split("/admin/")[1];
  const match = ALL_ITEMS.find((i) => i.path === slug);
  return match?.label || "Dashboard";
}

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("mkcg_admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    const stored = localStorage.getItem("mkcg_admin_user");
    if (stored) setUser(JSON.parse(stored));
  }, [navigate]);

  useEffect(() => {
  const handlePageShow = (event) => {
    if (event.persisted) {
      window.location.reload();
    }
  };
  window.addEventListener("pageshow", handlePageShow);
  return () => window.removeEventListener("pageshow", handlePageShow);
}, []);

  // Close the dropdown on outside click or Escape, so it behaves like a
  // normal menu instead of staying open until the user clicks Logout.
  useEffect(() => {
    if (!showUserMenu) return;

    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") setShowUserMenu(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showUserMenu]);

  const handleLogout = () => {
    localStorage.removeItem("mkcg_admin_token");
    localStorage.removeItem("mkcg_admin_user");
    navigate("/admin");
  };

  const activeLabel = currentLabelFromPath(location.pathname);
  const todayLabel = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  const noOutline = { outline: "none", boxShadow: "none" };

  return (
    <div className="mkcg-admin h-screen w-full flex overflow-hidden relative">
      <div className="aura" />
      <Sidebar onLogout={handleLogout} />

      <div className="flex-1 min-w-0 flex flex-col h-screen relative z-[1]">
        {/* Header */}
          <header className="mkcg-header h-14 shrink-0 flex items-center justify-between px-6">
          <div className="flex items-center gap-2.5">
            <p className="text-[14px] font-semibold uppercase tracking-[0.08em] leading-none whitespace-nowrap" style={{ color: "#0f766e" }}>
              MKCG Admin Console
            </p>
            <span className="text-slate-300 text-[14px] leading-none">/</span>
            <h2 className="font-display font-semibold text-[14px] leading-none whitespace-nowrap" style={{ color: "#0b1f3a" }}>
              {activeLabel}
            </h2>
          </div>

          <div className="flex items-center gap-4">
            <span className="hidden sm:inline-flex text-[12px] font-medium text-slate-400">
              {todayLabel}
            </span>

            <div className="relative" ref={userMenuRef} style={noOutline}>
              <button
                type="button"
                onClick={() => setShowUserMenu((v) => !v)}
                aria-haspopup="true"
                aria-expanded={showUserMenu}
                title="Account menu"
                style={noOutline}
                className="flex items-center gap-2.5 pl-4 pr-2.5 py-1.5 border-l border-slate-200 rounded-lg transition hover:bg-slate-50 !outline-none focus:!outline-none focus-visible:!outline-none active:!outline-none"
              >
                <div
                  className="w-9 h-9 rounded-full text-white flex items-center justify-center text-sm font-semibold shrink-0 shadow-sm"
                  style={{ background: "linear-gradient(135deg,#0b1f3a,#0f766e)" }}
                >
                  {user?.name?.charAt(0) || "U"}
                </div>
                <div className="hidden sm:flex items-center gap-1.5 leading-tight">
                  <p className="text-sm font-semibold" style={{ color: "#0b1220" }}>{user?.name || "Staff User"}</p>
                  <ChevronDown
                    size={14}
                    className={`text-slate-400 transition-transform shrink-0 ${showUserMenu ? "rotate-180" : ""}`}
                  />
                </div>
              </button>

              {showUserMenu && (
                <div
                  style={noOutline}
                  className="absolute right-0 top-[calc(100%+10px)] w-32 bg-white rounded-lg shadow-xl border border-slate-100 overflow-hidden z-50"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowUserMenu(false);
                      handleLogout();
                    }}
                    style={noOutline}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50 transition !outline-none focus:!outline-none"
                  >
                    <LogOut size={15} className="shrink-0" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Routed page content */}
        <main className="flex-1 overflow-y-auto px-6 pt-3 pb-6">
          <Outlet context={{ user }} />
        </main>
      </div>
    </div>
  );
}

// Used by App.js as the fallback element for any sidebar route that doesn't
// have a real page built yet — shows the correct label instead of a blank page.
export function AdminPlaceholderRoute() {
  const location = useLocation();
  const label = currentLabelFromPath(location.pathname);
  return <AdminPlaceholder label={label} />;
}
